import { ComponentClass, ComponentProps, Context, forwardRef, useContext, PropsWithChildren, ForwardedRef, createElement } from 'react';

type Contexts = {
    [key: string]: Context<any>
};

type ContextsValue<C extends Contexts> = {
    [K in keyof C]: C[K] extends Context<infer T> ? T : never
};

type RenderComponent<C extends Contexts, P = any> = ComponentClass<ContextsValue<C> & P>;

type RenderComponentNoContextProps<C extends Contexts, R extends RenderComponent<C>> = Omit<JSX.LibraryManagedAttributes<R, ComponentProps<R>>, keyof C>;

type ResultComponentProps<C extends Contexts, R extends RenderComponent<C>, P = RenderComponentNoContextProps<C, R>> = PropsWithChildren<Partial<ContextsValue<C>> & P>;

type ResultComponentPropsWithRef<C extends Contexts, R extends RenderComponent<C>, P = RenderComponentNoContextProps<C, R>> = ResultComponentProps<C, R, P> & { ref: ForwardedRef<InstanceType<R>> };

const WithContexts = <C extends Contexts, R extends RenderComponent<C>, P = RenderComponentNoContextProps<C, R>>({ contexts, component, forwarded }: { contexts: C, component: R, forwarded: ResultComponentPropsWithRef<C, R, P> }) => {
    const values = (Object.keys(contexts) as (keyof C)[]).reduce<ContextsValue<C>>((result, name) => ({
        ...result,
        // eslint-disable-next-line react-hooks/rules-of-hooks
        [name]: forwarded[name] || useContext(contexts[name])
    }), {} as ContextsValue<C>);

    return createElement(component, { ...forwarded, ...values });
};

export const withContexts = <C extends Contexts, R extends RenderComponent<C>, P = RenderComponentNoContextProps<C, R>>(contexts: C, component: R) => (
    forwardRef<InstanceType<R>, ResultComponentProps<C, R, P>>((props, ref) => (
        WithContexts({ contexts, component, forwarded: { ref, ...props } })
    ))
);
